/*
 * sni-alerte-stock.js — « M'avertir de la disponibilité » (bloc blocks/sni-alerte-stock.liquid)
 *
 * Deux rôles :
 *  1. Suivre le changement de taille. Horizon ne recharge que le sélecteur de
 *     variante ; chaque composant de la fiche reprend ensuite son propre fragment
 *     dans le HTML reçu avec l'événement `variant:update`. On fait comme
 *     `product-inventory.js`, pour que le formulaire apparaisse ou disparaisse selon
 *     que la taille choisie est épuisée ou non.
 *  2. Inscrire l'e-mail chez Klaviyo en alerte retour en stock sur CETTE variante,
 *     via l'API client (clé publique du compte, pas de secret). L'inscription
 *     déclenche le flow « Subscribed to Back in Stock » de la marque.
 *     Doc : https://developers.klaviyo.com/en/docs/how_to_set_up_custom_back_in_stock
 */
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

const API = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
// Révision d'API figée : la forme du corps ci-dessous est celle de cette révision.
const REVISION = '2024-06-15';

class SniAlerteStock extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.section?.addEventListener(ThemeEvents.variantUpdate, this.majVariante);
    this.addEventListener('submit', this.envoyer);
  }

  disconnectedCallback() {
    this.section?.removeEventListener(ThemeEvents.variantUpdate, this.majVariante);
    this.removeEventListener('submit', this.envoyer);
  }

  /** Reprend le fragment du bloc dans le HTML de la nouvelle variante. */
  majVariante = (event) => {
    const data = event.detail?.data;
    if (!data?.html) return;
    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      // Un autre produit de la page (recommandations, ajout rapide) : pas nous.
      return;
    }
    const neuf = data.html.querySelector('sni-alerte-stock');
    if (neuf) morph(this, neuf, { childrenOnly: true });
  };

  envoyer = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('sni-alerte__form')) return;
    event.preventDefault();

    const champ = form.querySelector('input[type="email"]');
    const valider = form.querySelector('.sni-alerte__valider');
    const variante = form.closest('[data-variante]')?.dataset.variante;
    if (!champ || !variante) return;

    const email = champ.value.trim();
    if (!email || !champ.checkValidity()) {
      this.statut(form, form.dataset.invalide, 'erreur');
      champ.focus();
      return;
    }

    valider.disabled = true;
    try {
      const reponse = await fetch(`${API}?company_id=${encodeURIComponent(this.dataset.cle)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: REVISION },
        body: JSON.stringify({
          data: {
            type: 'back-in-stock-subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email } } },
              channels: ['EMAIL'],
            },
            relationships: {
              variant: {
                data: { type: 'catalog-variant', id: `$shopify:::$default:::${variante}` },
              },
            },
          },
        }),
      });
      // Klaviyo répond 202 sans corps quand l'inscription est acceptée.
      if (!reponse.ok) throw new Error(`Klaviyo ${reponse.status}`);
      champ.value = '';
      this.statut(form, form.dataset.ok, 'ok');
    } catch (erreur) {
      console.warn('[sni-alerte-stock]', erreur);
      this.statut(form, form.dataset.erreur, 'erreur');
    } finally {
      valider.disabled = false;
    }
  };

  statut(form, message, etat) {
    const zone = form.querySelector('.sni-alerte__statut');
    if (!zone) return;
    zone.textContent = message || '';
    zone.dataset.etat = etat;
    zone.hidden = !message;
  }
}

if (!customElements.get('sni-alerte-stock')) {
  customElements.define('sni-alerte-stock', SniAlerteStock);
}
